import type { PackageData } from '@forge/rendering-engine';

import type { View as ForgeView } from '../../../types/forge';

/**
 * Compose a `PackageData` from the live `app-data` query result.
 *
 * This mirrors what `fetchAllAppData` (in packagingService) does at
 * package-time, but in memory and synchronously — the iframe needs an
 * up-to-date snapshot on every Hasura refetch so the canvas reflects
 * saves immediately.
 *
 * Phase 5+: when the editor adds an in-memory dirty buffer (changes not
 * yet saved to Hasura), this function should overlay that buffer on top
 * of the server data so the canvas shows preview-of-changes too. Today
 * we save eagerly so the server-side data is always current.
 */
export interface AppDataShape {
  FrontendApp_by_pk: {
    id: string;
    org_id: string;
    name: string;
    main_view_id: string | null;
    theme: unknown | null;
    app_instances: Array<{ key: string; value: string }>;
  } | null;
  View: ForgeView[];
  InputMask?: Array<{ id: string; name: string; pattern: string; placeholder: string | null }>;
}

export function composeSnapshot(appData: AppDataShape): PackageData | null {
  const fa = appData.FrontendApp_by_pk;
  if (!fa || !fa.main_view_id) return null;

  return {
    frontend_app: {
      id: fa.id,
      name: fa.name,
      org_id: fa.org_id,
      main_view_id: fa.main_view_id,
      theme: fa.theme as any,
      app_instances: fa.app_instances,
    },
    app_views: appData.View.map((v) => ({
      id: v.id,
      Type: v.Type,
      name: v.name,
      mask_id: v.mask_id ?? undefined,
      // The engine reads viewStyle.properties via its own resolvePropertyMap;
      // pass the JSONB blob unchanged.
      viewStyle: (v.viewStyle?.properties ?? null) as any,
      viewSource: (v.viewSource ?? undefined) as any,
      child_views: (v.child_views ?? []).map((c) => ({
        related_view_id: c.related_view_id,
        sort: c.sort,
      })),
      interactions: (v.interactions ?? []).map((vi) => ({
        type: vi.interaction.type,
        actions: (vi.interaction.Interaction_Actions ?? []).map((ia) => ({
          type: ia.Action.Type,
          value: ia.Action.Value ?? '',
          priority: ia.priority,
        })),
      })),
    })) as any,
    masks: (appData.InputMask ?? []) as any,
  };
}
